/**
 * Teilbare Links auf Corporate-Dokumente (#516).
 *
 * Ein geteilter Link ist eine ADRESSE, keine Berechtigung: er trägt kein Token
 * und schaltet nichts frei. Wer ihn öffnet, sieht das Dokument genau dann, wenn
 * er es ohnehin sehen dürfte — geprüft wird serverseitig beim Öffnen.
 *
 * Der Link ist bewusst wirt-neutral (`/d/<id>`). Dieselbe App läuft in zwei
 * Wirten (#504): als Fenster im Desktop (`/desktop`) und shell-frei unter
 * `/drive`. Welcher davon zuständig ist, hängt am EMPFÄNGER, nicht am Absender
 * — stünde der Wirt im Link, verschickte man seine eigenen Rechte mit.
 *
 * Die beiden Wirte lesen Deep-Link-Parameter unterschiedlich, und genau das ist
 * der Grund, warum die Auflösung an einer Stelle liegt statt an zweien:
 *
 *   Desktop     Hash mit Kurz-Aliassen  →  /desktop#/w=drive2:d=<id>,ka=<ka>
 *   Standalone  Query mit Langnamen     →  /drive?documentId=<id>&knowledgeArea=<ka>
 *
 * (`useStandaloneAppHost` reicht die Query unverändert durch, `paramsFromSearch`
 * löst keine Aliasse auf — dort MÜSSEN die langen Namen stehen.)
 */
import { deepLinkService } from '@/services/deep-link-service'

/** Route-Muster des Resolvers. Einzige Stelle, an der der Pfad definiert ist. */
export const DOCUMENT_LINK_ROUTE = '/d/:documentId'

/** App-interner Pfad des Resolvers für ein Dokument. */
export function documentLinkPath(documentId: string): string {
  return `/d/${encodeURIComponent(documentId)}`
}

/**
 * Der Link zum Kopieren. Absolut, weil er die Anwendung verlässt (Mail, Teams).
 *
 * `origin` ist injizierbar, damit die Funktion ohne DOM testbar bleibt.
 */
export function documentLinkUrl(
  documentId: string,
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): string {
  return `${origin}${documentLinkPath(documentId)}`
}

/** Anwendungs-Zuweisungen, wie sie in `allowed_applications` stehen (#504). */
const DESKTOP_APPLICATION = 'webos'
const STANDALONE_APPLICATION = 'pdb'

/**
 * Wohin ein Empfänger mit diesen Zuweisungen für dieses Dokument gehört.
 *
 * `null` heißt: Der Nutzer hat keinen Wirt, in dem sich das Dokument zeigen
 * ließe. Das ist ein anderer Fall als "kein Zugriff auf das Dokument" und
 * verdient eine eigene Meldung — sonst sucht jemand den Fehler beim Dokument,
 * obwohl ihm schlicht die Anwendung fehlt.
 *
 * Der Desktop hat Vorrang: wer beides hat, arbeitet dort.
 */
export function documentTargetFor(
  documentId: string,
  allowedApplications: string[],
  knowledgeArea?: string,
): string | null {
  if (allowedApplications.includes(DESKTOP_APPLICATION)) {
    const hash = deepLinkService.generateUrl([
      { appId: 'drive2', params: { documentId, knowledgeArea } },
    ])
    return `/desktop${hash}`
  }

  if (allowedApplications.includes(STANDALONE_APPLICATION)) {
    const query = new URLSearchParams({ documentId })
    if (knowledgeArea) query.set('knowledgeArea', knowledgeArea)
    return `/drive?${query.toString()}`
  }

  return null
}
